/**
 * App — root component for Mission Control.
 *
 * Auth flow:
 * 1. Fetch /api/config (no auth) to learn fleet_slug + console_url.
 * 2. Check for a bearer token in the URL hash (#token=<jwt>) or sessionStorage.
 *    - URL hash: store in sessionStorage, strip from URL.
 * 3. If still no token, try to mint one from the console session cookie
 *    (mintFleetToken). This is what makes DEEP LINKS work: a shared chat
 *    permalink or a Slack @mention link opened in a fresh tab has no token in
 *    sessionStorage, and sending the operator to the console to get one would
 *    reopen MC at its root and throw the link away. Minting in place preserves
 *    the URL. It succeeds whenever they have a live console session and a grant
 *    for this fleet — i.e. exactly the people already allowed in.
 * 4. If the mint also fails → show Unauthenticated.
 * 5. GET /api/me to verify → 401 → Unauthenticated, 200 → initialise
 *    tokenManager then render shell.
 *
 * Silent refresh (ADR-023 Option A):
 * After getMe() succeeds, tokenManager.init() is called. The manager schedules
 * a proactive refresh ~60s before the token expires, and also triggers a refresh
 * on focus / visibilitychange (catches laptop-sleep gaps). If a refresh fails
 * (console session gone), onExpired() transitions the app back to unauthenticated.
 * The api client additionally attempts a refresh on any 401 response and retries
 * the request once.
 *
 * Phase 2/3: the authenticated view mounts AttentionProvider above BrowserRouter and AppShell.
 */
import { useEffect, useState, useCallback } from 'react'
import { BrowserRouter, Routes } from 'react-router-dom'
import { AppShell } from './components/AppShell'
// The whole route table, as values. It lives in its own module so the contract
// tests can read the routes this app renders instead of parsing them out of this
// file's source text — see appRoutes.tsx.
import { appRoutes } from './appRoutes'
import { Unauthenticated } from './pages/Unauthenticated'
import { AuthContext } from './context/AuthContext'
import { CoreStatusProvider } from './context/CoreStatusContext'
import { AttentionProvider } from './context/AttentionContext'
import { HeaderSlotProvider } from './context/HeaderSlotContext'
import { api, UnauthorizedError, type MeResponse, type ClientConfig } from './api/client'
import { tokenManager, mintFleetToken } from './auth/tokenManager'
import { rememberReturnPath, takeReturnPath } from './auth/returnPath'
import { fleetDisplayName } from './utils/fleetName'

type AppState =
  | { phase: 'loading' }
  | { phase: 'unauthenticated'; config: ClientConfig }
  | { phase: 'authenticated'; user: MeResponse; config: ClientConfig }
  | { phase: 'error'; message: string }

function extractTokenFromHash(): string | null {
  const hash = window.location.hash.slice(1)
  const params = new URLSearchParams(hash)
  return params.get('token')
}

function stripTokenFromURL() {
  const url = new URL(window.location.href)
  url.hash = ''
  window.history.replaceState(null, '', url.toString())
}

const TOKEN_SESSION_KEY = 'mc_token'

function loadToken(): string | null {
  const hashToken = extractTokenFromHash()
  if (hashToken) {
    sessionStorage.setItem(TOKEN_SESSION_KEY, hashToken)
    stripTokenFromURL()
    return hashToken
  }
  return sessionStorage.getItem(TOKEN_SESSION_KEY)
}

function App() {
  const [state, setState] = useState<AppState>({ phase: 'loading' })

  // Called by the tokenManager when a refresh attempt fails (console session gone).
  const handleExpired = useCallback((config: ClientConfig) => {
    setState({ phase: 'unauthenticated', config })
  }, [])

  useEffect(() => {
    let cancelled = false

    async function init() {
      let config: ClientConfig
      try {
        config = await api.getConfig()
      } catch {
        if (!cancelled) setState({ phase: 'error', message: 'Failed to load config from server.' })
        return
      }

      // Pre-branch on purpose: every phase that has a config passes through
      // here, so one assignment covers authenticated and unauthenticated alike.
      // The pre-config error path returns above and keeps index.html's static
      // title, by design. Guarded by `cancelled` because AppShell writes the
      // fuller `<page> - <fleet>` title: under StrictMode two init() calls are in
      // flight, and a cancelled one resolving late would overwrite the shell's
      // title with the bare name, which the shell's effect would not re-run to
      // repair. Guarded per fleetName.ts's empty-name contract, and named the
      // same `fleetName` AppShell gives the other half of that title.
      const fleetName = fleetDisplayName(config.fleet_slug)
      if (!cancelled && fleetName) document.title = fleetName

      let token = loadToken()
      if (!token) {
        // Nothing in this tab — but the operator may still have a live console
        // session. Mint from it in place rather than sending them to the console,
        // which would reopen MC at its root and discard whatever deep link they
        // followed to get here (a shared chat permalink, a Slack @mention).
        token = await mintFleetToken(config.console_url, config.fleet_slug)
        if (cancelled) return
        if (token) sessionStorage.setItem(TOKEN_SESSION_KEY, token)
      }
      if (!token) {
        // ADR-210: keep a deep link (a permanent file link) across the trip to the console.
        rememberReturnPath(window.location.pathname, window.location.search)
        if (!cancelled) setState({ phase: 'unauthenticated', config })
        return
      }

      try {
        const user = await api.getMe(token)
        if (!cancelled) {
          // Initialise the token manager BEFORE updating state so that any api
          // call triggered by the new render already has a valid token source.
          tokenManager.init({
            token,
            consoleUrl: config.console_url,
            fleetSlug: config.fleet_slug,
            onExpired: () => handleExpired(config),
          })
          // ADR-210: arrived at the root from the console with a path remembered — go there.
          const back = takeReturnPath(window.location.pathname)
          if (back) window.history.replaceState(null, '', back)
          setState({ phase: 'authenticated', user, config })
        }
      } catch (err) {
        sessionStorage.removeItem(TOKEN_SESSION_KEY)
        if (err instanceof UnauthorizedError) {
          rememberReturnPath(window.location.pathname, window.location.search)
          if (!cancelled) setState({ phase: 'unauthenticated', config })
        } else {
          if (!cancelled) setState({ phase: 'error', message: 'Authentication check failed.' })
        }
      }
    }

    void init()
    return () => {
      cancelled = true
      // Stop the manager when the component unmounts (e.g. HMR, strict-mode
      // double-invoke). A real unmount also cleans up timers/listeners.
      tokenManager.stop()
    }
  }, [handleExpired])

  if (state.phase === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading...</p>
      </div>
    )
  }

  if (state.phase === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-red-200 rounded-lg p-6 max-w-sm text-center">
          <p className="text-sm font-semibold text-red-700">Error</p>
          <p className="text-sm text-red-600 mt-1">{state.message}</p>
        </div>
      </div>
    )
  }

  if (state.phase === 'unauthenticated') {
    return (
      <Unauthenticated
        consoleURL={state.config.console_url}
        fleetSlug={state.config.fleet_slug}
      />
    )
  }

  // Authenticated: wrap in AttentionProvider + BrowserRouter + shell + routes.
  const { user, config } = state

  return (
    <AuthContext.Provider value={{ user }}>
      <CoreStatusProvider>
        <AttentionProvider>
        <BrowserRouter>
          {/* The header slot must wrap BOTH the shell (which renders it) and
              the routes (which fill it) — a page's own nav lives on the header
              row rather than costing a band of its own. */}
          <HeaderSlotProvider>
          <AppShell
            user={user}
            fleetSlug={config.fleet_slug}
            consoleURL={config.console_url}
          >
            <Routes>{appRoutes}</Routes>
          </AppShell>
          </HeaderSlotProvider>
        </BrowserRouter>
        </AttentionProvider>
      </CoreStatusProvider>
    </AuthContext.Provider>
  )
}

export default App
